import { computed, unref } from 'vue';
import { MESSAGE_TYPES } from 'dashboard/components-next/message/constants';

/**
 * Matches the sender prefix that WhatsApp group gateways prepend to the
 * message body, e.g. `Bruno de Padua [5527988621092]: 2,2+`.
 * The name is lazy so it stops at the last bracketed phone number.
 */
const GROUP_SENDER_PATTERN = /^(.+?) \[(\d+)\]:\s*/;

/**
 * Placeholder labels the gateway sends in place of media it cannot inline,
 * e.g. `[sticker]` or `📁 imagemessage`. The attachment itself already conveys
 * the media, so WhatsApp shows no such label and neither should we.
 */
const MEDIA_PLACEHOLDER_PATTERN =
  /^(\[(sticker|image|audio|video|document|file)\]|📁\s*\S*message)$/i;

/**
 * A reaction reaches us as a whole message — `❤️ [ reação ]` quoting the
 * message it belongs to. WhatsApp shows these stacked on the original bubble
 * instead, so they are pulled out of the timeline and aggregated there.
 */
const REACTION_PATTERN = /^(\S+)\s*\[\s*rea[çc][ãa]o\s*\]$/i;

/**
 * Tailwind text tokens used to colour participant names, mirroring the
 * per-participant colours WhatsApp Web assigns. These are theme aware, so
 * they stay legible in both light and dark mode.
 */
// Green is left out on purpose: it is the colour of mentions and links, and a
// participant name in the same tone would read as one.
const SENDER_COLORS = [
  'text-n-amber-11',
  'text-n-iris-11',
  'text-n-ruby-11',
  'text-n-blue-11',
  'text-n-slate-11',
];

/**
 * Picks a stable colour for a participant name, so the same person keeps the
 * same colour across bubbles and quoted replies.
 *
 * @param {String} name - Participant name
 * @returns {String} Tailwind text colour class
 */
export const colorForName = name => {
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) {
    hash = (hash * 31 + name.charCodeAt(i)) % 1000003;
  }
  return SENDER_COLORS[hash % SENDER_COLORS.length];
};

/**
 * Splits a group sender prefix out of a message body.
 *
 * @param {String} content - Raw message content
 * @returns {{name: String, phone: String, body: String}|null}
 */
export function parseGroupSender(content) {
  if (!content) return null;

  const match = content.match(GROUP_SENDER_PATTERN);
  if (!match) return null;

  const [prefix, name, phone] = match;

  return { name, phone, body: content.slice(prefix.length) };
}

/**
 * Drops a media placeholder label so the message renders as pure media.
 *
 * @param {String} body - Message body, already free of the sender prefix
 * @param {Boolean} hasAttachments - Whether the message carries an attachment
 * @returns {String}
 */
export function stripMediaPlaceholder(body, hasAttachments) {
  if (!hasAttachments || !body) return body;

  return MEDIA_PLACEHOLDER_PATTERN.test(body.trim()) ? '' : body;
}

/**
 * Reads a message as a reaction to another one.
 *
 * @param {Object} message - Message in camelCase form
 * @returns {{emoji: String, name: String|null, targetId: Number}|null}
 * Null when the message is not a reaction or does not say what it reacts to.
 */
export function parseReaction(message) {
  const targetId = message?.contentAttributes?.inReplyTo;
  if (!targetId) return null;

  const parsed = parseGroupSender(message.content);
  const match = (parsed?.body ?? message?.content ?? '').match(
    REACTION_PATTERN
  );
  if (!match) return null;

  return { emoji: match[1], name: parsed?.name ?? null, targetId };
}

/**
 * Exposes the group participant behind an incoming message, so the bubble can
 * render the name as its own coloured heading instead of leaving it inline.
 * Only incoming messages are parsed — outgoing ones are written by agents and
 * never carry the gateway prefix.
 *
 * @param {import('vue').Ref<String>} content - The message content
 * @param {import('vue').Ref<Number>} messageType - The message type
 * @param {import('vue').Ref<Array>} [attachments] - The message attachments
 * @returns {{groupSender: import('vue').ComputedRef<Object|null>,
 *   contentWithoutSender: import('vue').ComputedRef<String>}}
 */
export function useGroupSender(content, messageType, attachments) {
  const groupSender = computed(() => {
    if (unref(messageType) !== MESSAGE_TYPES.INCOMING) return null;

    const parsed = parseGroupSender(unref(content));
    if (!parsed) return null;

    return { ...parsed, colorClass: colorForName(parsed.name) };
  });

  const contentWithoutSender = computed(() => {
    const body = groupSender.value?.body ?? unref(content);

    return stripMediaPlaceholder(body, unref(attachments)?.length > 0);
  });

  return { groupSender, contentWithoutSender };
}
